import { useMemo } from "react";
import moment from "moment";
import {
  Switch,
  Typography,
  Box,
  FormControlLabel,
  Button,
  ButtonGroup
} from "@material-ui/core";
import { CheckboxGroup, Color, DatePicker, SelectMenu } from "@bonliva-crm/ui";
import locale, { dateFormat } from "@bonliva-crm/locale";
import { LastContactType, SelectType } from "@bonliva-crm/graphql";
import { useFormField } from "@clientUtils";
import { FilterWithFavouriteProps } from "../utils/filters-record-types";
import { CollapsibleFavFilter } from "./_base/collapsible-fav-filter";
import * as yup from "yup";

export type LastContactFilterData = {
  dates: { from: Date | null; to: Date | null };
  myActivity: boolean;
  type: LastContactType[];
  selectType: SelectType;
};

export type LastContactFilterProps = FilterWithFavouriteProps;

export const LastContactFilter = (props: LastContactFilterProps) => {
  const title = props.label ?? locale.LastContactDate;
  const options = useMemo(() => {
    return [
      LastContactType.Comment,
      LastContactType.Email,
      LastContactType.Interest,
      LastContactType.NoCall,
      LastContactType.NoCallAttempts,
      LastContactType.NoComment,
      LastContactType.NoContactAttempts,
      LastContactType.Noanswer,
      LastContactType.Registered,
      LastContactType.SMS,
      LastContactType.Call
    ].toLabelValue((v) => locale.lastContactType[v]) as Option[];
  }, []);

  const { field, fieldState, resetField, setFieldValue } =
    useFormField<LastContactFilterData | null>(props.name);

  const valueFrom = field.value?.dates?.from;
  const valueTo = field.value?.dates?.to;
  const selectType = field.value?.selectType;
  const type = field.value?.type;

  const setMyActivity = (value: boolean) => {
    setFieldValue(
      { ...field.value, myActivity: value },
      { shouldDirty: true, shouldValidate: true }
    );
  };

  const setSelectType = (value: SelectType) => {
    setFieldValue(
      { ...field.value, selectType: value },
      { shouldDirty: true, shouldValidate: true }
    );
  };

  const setType = (value: LastContactType[]) => {
    setFieldValue(
      { ...field.value, type: value },
      { shouldDirty: true, shouldValidate: true }
    );
  };
  const setDate = (value: Date, name: string) => {
    setFieldValue(
      { ...field.value, dates: { ...field.value.dates, [name]: value } },
      { shouldDirty: true, shouldValidate: true }
    );
  };

  const content = (
    <Box>
      <FormControlLabel
        control={
          <Switch
            checked={field.value?.myActivity ?? false}
            onChange={(_, v) => setMyActivity(v)}
            color="primary"
          />
        }
        label={locale.ShowOnlyMyActivity}
        style={{ margin: 0 }}
      />

      <Box display="flex" alignItems="flex-end" gridGap="8px" mt={3}>
        <DatePicker
          fullWidth
          isClearable={!!valueFrom}
          value={valueFrom || null}
          onChange={(v) => {
            setDate(v, "from");
          }}
          error={!!fieldState.error}
          DatePickerProps={{
            maxDate: valueTo
          }}
          label={locale.StartDate}
        />

        <DatePicker
          fullWidth
          isClearable={!!valueFrom}
          value={valueTo || null}
          onChange={(v) => {
            setDate(v, "to");
          }}
          DatePickerProps={{
            minDate: valueFrom
          }}
          label={locale.EndDate}
        />
      </Box>

      <Box mt={3}>
        <ButtonGroup>
          <Button
            color={selectType === SelectType.ANY ? "primary" : undefined}
            variant="contained"
            onClick={() => setSelectType(SelectType.ANY)}
          >
            {locale.All}
          </Button>

          <Button
            color={selectType === SelectType.AND ? "primary" : undefined}
            variant="contained"
            onClick={() => setSelectType(SelectType.AND)}
          >
            {locale.And}
          </Button>
        </ButtonGroup>
      </Box>

      <Box mt={1}>
        <CheckboxGroup
          value={type || []}
          onChange={(_, v) => setType(v as LastContactType[])}
          options={options}
        />
      </Box>
    </Box>
  );

  if (props.setFavorite) {
    return (
      <CollapsibleFavFilter
        title={title}
        favorite={props.favorite}
        onSetFavorite={props.setFavorite}
        isClearable={fieldState.isDirty}
        onClear={resetField}
        isDirty={fieldState.isDirty}
        initialExpanded={fieldState.isDirty}
      >
        {content}
      </CollapsibleFavFilter>
    );
  }

  return (
    <SelectMenu
      badgeLabel={
        !!valueFrom || !!valueTo
          ? `${valueFrom ? moment(valueFrom).format(dateFormat) : ""} - ${
              valueTo ? moment(valueTo).format(dateFormat) : ""
            }`
          : undefined
      }
      placeholder={
        <Typography
          variant="body1"
          style={{ color: Color.DetailsInput, lineHeight: "calc(1rem + 4px)" }}
        >
          {title}
        </Typography>
      }
      openByDefault={false}
      showPlaceholderOnExpand={true}
      isClearable={fieldState.isDirty}
      onClear={resetField}
      content={content}
    />
  );
};

type Option = { label: string; value: LastContactType };

export const lastContactFilterSchema = yup.object().shape({
  lastContact: yup.object().shape({
    dates: yup.object().shape({
      from: yup.date().required()
    })
  })
});
